import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import { useStyles } from "../../theme/appStyles";
import {
  Avatar,
  Collapse,
  Grid,
  InputAdornment,
  ListItemButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserImg from "../../Images/avatar.png";
import { useMemo, useState } from "react";
import { useThemeContext } from "./ThemeContext";
import Notification from "../../Components/Popup/notification";
import LogoMain from "../../Images/logo.png";
import { MdDarkMode } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import Profile from "../../Components/Popup/profile";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Privileges } from "../../Core/Enum/enum";
import useUser from "../../Core/hook/useUser";
import {
  useGetUserQuery,
  useGetConversationsNotificationQuery,
  useGetCustomerQuery,
} from "../../Api/attoDeskApi";
import { IToDo, IUserInfo, ICustomer } from "../../Api/Interface/api.interface";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import useApp from "../../utils/useApp";
import Groups2Icon from "@mui/icons-material/Groups2";
// import AddTaskIcon from "@mui/icons-material/AddTask";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import PolicyIcon from "@mui/icons-material/Policy";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { MdOutlineNotificationsNone } from "react-icons/md";
import Badge from "@mui/material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AddIcon from "@mui/icons-material/Add";
import { toProperCase } from "../../Components/Validation/toProperCase";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const drawerWidth = 280;
const headerHeight = 100;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: headerHeight,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    paddingLeft: 0,
  }),
  ...(!open && {
    marginLeft: 0,
    width: "100%",
    paddingLeft: "75px",
    [theme.breakpoints.down("md")]: {
      paddingLeft: "0px",
    },
  }),
}));

const IsNotMobileDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
    },
  }),
}));

const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: headerHeight,
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.topbarButton.drawerHeader,
  paddingLeft: "75px",
  margin: "0px",
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  flexShrink: 0,
}));

const DrawerContent = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.mainMenuNav.mainNavBg,
  overflowY: "auto",
  // overflowX: "hidden", // Disable horizontal scrolling
  flexGrow: 1,
  "&::-webkit-scrollbar": {
    width: "0px",
    color: "#fcf2eb",
  },
  // "&:hover::-webkit-scrollbar": {
  //   width: "4px",
  // },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#fcf2eb",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#fcf2eb",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
}));

const PaddingBody = styled("div")(({ theme }) => ({
  height: headerHeight,
}));

type SubmenuItem = {
  topBarTitle?: string;
  title: string;
  path: string;
  newPage?: boolean;
  icon: React.ReactElement;
  privilege: string;
};

type menuList = {
  topBarTitle: string;
  title: string;
  icon: React.ReactElement;
  path: string;
  newPage?: boolean;
  submenuOpen?: boolean;
  submenus?: SubmenuItem[];
  privilege: string;
};

const MainMenuList: {
  comomTitle: string;
  menuList?: menuList[];
}[] = [
  {
    comomTitle: "General",
    menuList: [
      {
        title: "Dashboard",
        topBarTitle: "Welcome!",
        icon: <MenuIcon />,
        path: "/dashboard",
        submenuOpen: false,
        privilege: Privileges.DashboardView,
      },
      {
        title: "Client",
        topBarTitle: "Client List",
        icon: <PeopleIcon />,
        path: "/customer/list",
        submenuOpen: false,
        privilege: Privileges.ClientView,
      },
      {
        title: "Policies",
        topBarTitle: "Policies List",
        icon: <AddModeratorIcon />,
        path: "/policy-list",
        submenuOpen: false,
        privilege: Privileges.PoliciesView,
      },
      {
        title: "Report",
        topBarTitle: "Report List",
        icon: <AssignmentIcon />,
        path: "/policy-scenarios/list",
        submenuOpen: false,
        privilege: Privileges.CoverageView,
      },
      {
        title: "Meeting",
        topBarTitle: "Meeting List",
        icon: <GroupsIcon />,
        path: "/client/meeting",
        submenuOpen: false,
        privilege: Privileges.ClientView,
      },
      {
        title: "Task",
        topBarTitle: "Task List",
        icon: <AddTaskIcon />,
        path: "/client/task",
        submenuOpen: false,
        privilege: Privileges.ClientView,
      },
      {
        title: "ToDo",
        topBarTitle: "ToDo List",
        icon: <PlaylistAddCheckIcon />,
        path: "/conversations/list",
        submenuOpen: false,
        privilege: Privileges.ToDoView,
      },
      {
        title: "Newsletter",
        topBarTitle: "Newsletter",
        icon: <MarkEmailUnreadIcon />,
        path: "/client/newsletters",
        submenuOpen: false,
        privilege: Privileges.NewslettersView,
      },
      {
        title: "Workshop",
        topBarTitle: "Workshop",
        icon: <HomeRepairServiceIcon />,
        path: "/workshop/list",
        submenuOpen: false,
        privilege: Privileges.WorkshopView,
      },
      {
        title: "Policy Report",
        topBarTitle: "Policy Report Generate",
        icon: <PictureAsPdfIcon />,
        path: "/client/report/generate",
        submenuOpen: false,
        privilege: Privileges.ClientView,
      },
    ],
  },

  {
    comomTitle: "Other",
    menuList: [
      // {
      //   title: "Products",
      //   topBarTitle: "Products List",
      //   icon: <PolicyIcon />,
      //   path: "/policy/list",
      //   submenuOpen: false,
      //   privilege: Privileges.PoliciesView,
      // },
      // {
      //   title: "Covergae Type",
      //   topBarTitle: "Covergae Type List",
      //   icon: <CategoryIcon />,
      //   path: "/category/list",
      //   submenuOpen: false,
      //   privilege: Privileges.CoverageView,
      // },
      {
        title: "Dropdown List",
        topBarTitle: "Dropdown List",
        icon: <ArrowDropDownCircleOutlinedIcon />,
        path: "/dropdown",
        submenuOpen: true,
        privilege: Privileges.PoliciesView,
        submenus: [
          {
            title: "Products",
            topBarTitle: "Products List",
            icon: <PolicyIcon />,
            path: "/policy/list",
            privilege: Privileges.PoliciesView,
          },
          {
            title: "Covergae Type",
            topBarTitle: "Covergae Type List",
            icon: <CategoryIcon />,
            path: "/category/list",
            privilege: Privileges.CoverageView,
          },
          {
            topBarTitle: "Insurance Company",
            title: "Insurance Company",
            icon: <MenuIcon />,
            path: "/brand/list",
            privilege: Privileges.InsuranceCompanyView,
          },
          {
            topBarTitle: "Client Type",
            title: "Client Type",
            icon: <MenuIcon />,
            path: "/client_type/list",
            privilege: Privileges.ClientTypeView,
          },
        ],
      },
      {
        title: "Setting",
        topBarTitle: "Setting",
        icon: <SettingsOutlinedIcon />,
        path: "/looping",
        submenuOpen: true,
        privilege: Privileges.SettingView,
        submenus: [
          {
            topBarTitle: "User",
            title: "User",
            icon: <MenuIcon />,
            path: "/user/list",
            privilege: Privileges.UserView,
          },
          {
            topBarTitle: "User Role",
            title: "User Role",
            icon: <MenuIcon />,
            path: "/user/role/list",
            privilege: Privileges.UserView,
          },
          {
            topBarTitle: "Privilege",
            title: "Privilege",
            icon: <MenuIcon />,
            path: "/user/privilege/list",
            privilege: Privileges.UserView,
          },
          {
            topBarTitle: "Branch List",
            title: "Branch",
            icon: <MenuIcon />,
            path: "/branch/list",
            privilege: Privileges.BranchView,
          },
          {
            topBarTitle: "Client Report Setting",
            title: "Client Report Setting",
            icon: <MenuIcon />,
            path: "/client/report/setting",
            privilege: Privileges.CompanyView,
          },
          {
            topBarTitle: "Company",
            title: "Company",
            icon: <MenuIcon />,
            path: "/setting",
            privilege: Privileges.CompanyView,
          },
          {
            topBarTitle: "Email",
            title: "Email",
            icon: <EmailIcon />,
            path: "/email/setting/list",
            privilege: Privileges.EmailView,
          },
          {
            topBarTitle: "Newsletter",
            title: "Newsletter",
            icon: <MarkEmailUnreadIcon />,
            path: "/client/newsletters/list",
            privilege: Privileges.NewslettersView,
          },
          {
            topBarTitle: "Theme Color",
            title: "Theme Color",
            icon: <MenuIcon />,
            path: "/theme-color",
            privilege: Privileges.ThemeView,
          },
        ],
      },
    ],
  },
  {
    comomTitle: "Staff",
    menuList: [
      // {
      //   title: "Punch Details",
      //   topBarTitle: "Punch Details List",
      //   icon: <PunchClockIcon />,
      //   path: "/punch/list",
      //   submenuOpen: false,
      //   privilege: Privileges.PunchDetailsView,
      // },
      // {
      //   title: "Schedule",
      //   topBarTitle: "Schedule & Reschedule List",
      //   icon: <EditCalendarIcon />,
      //   path: "/Schedule/list/one-day",
      //   submenuOpen: false,
      //   privilege: Privileges.ScheduleView,
      // },
      // {
      //   topBarTitle: "Reschedule List",
      //   title: "Reschedule",
      //   icon: <EditCalendarIcon />,
      //   path: "/re-Schedule/list",
      //   privilege: Privileges.ScheduleView,
      // },
      // {
      //   title: "Clock In/Out",
      //   topBarTitle: "Clock In/Out List",
      //   icon: <ChecklistRtlIcon />,
      //   path: "/schedule/list",
      //   submenuOpen: false,
      //   privilege: Privileges.ScheduleView,
      // },
      // {
      //   title: "Payroll",
      //   topBarTitle: "Payroll List",
      //   icon: <ListAltIcon />,
      //   path: "/payroll/list",
      //   submenuOpen: false,
      //   privilege: Privileges.PayrollView,
      // },
      {
        topBarTitle: "Staff List",
        title: "Staff",
        icon: <Groups2Icon />,
        path: "/team/list",
        privilege: Privileges.StaffView,
      },
      {
        title: "Additional",
        topBarTitle: "Position/Department",
        icon: <AddIcon />,
        path: "/sc/othe",
        submenuOpen: true,
        privilege: Privileges.OtherView,
        submenus: [
          {
            topBarTitle: "Department List",
            title: "Department",
            icon: <MenuIcon />,
            path: "/department/list",
            privilege: Privileges.DepartmentView,
          },
          {
            topBarTitle: "Position List",
            title: "Position",
            icon: <MenuIcon />,
            path: "/position/list",
            privilege: Privileges.PositionView,
          },
        ],
      },
    ],
  },
];

export default function MiniDrawer({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { userHasRight } = useUser();
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [open, setOpen] = React.useState(true);

  const [notificationPopUp, setNotificationPopUp] =
    React.useState<HTMLElement | null>(null);
  const { mode, toggleTheme } = useThemeContext();
  const [profilePopUp, setProfilePopUp] = useState<HTMLElement | null>(null);
  const openProfilePopUp = Boolean(profilePopUp);
  const openNotificationPopUp = Boolean(notificationPopUp);

  const [currentMenuName, setCurrentMenuName] = React.useState("");
  const [hoveredMenuName, setHoveredMenuName] = React.useState("");
  const [expandMenu, setExpandMenu] = React.useState<string>("");
  const [topBarTitle, setTopBarTitle] = useState("");
  const [hoveredSubmenu, setHoveredSubmenu] = React.useState("");
  const [selectedSubmenu, setSelectedSubmenu] = React.useState("");
  const { currentMenuNameFullScreen } = useApp();

  const { data } = useGetUserQuery();

  const {
    data: noificationData,
    isLoading,
    isError,
    refetch,
  } = useGetConversationsNotificationQuery(
    { searchText: "", staffId: "", id: "" },
    {
      pollingInterval: 120000,
    }
  );

  const { data: allclientData, isLoading: allcilentIsLoading } =
    useGetCustomerQuery({
      searchText: "",
      id: "",
    });

  const userRole = React.useMemo(() => {
    if (data?.data) {
      const userData = data.data as IUserInfo;
      return userData.user_role_name;
    }
    return "";
  }, [data]);

  const username = React.useMemo(() => {
    if (data?.data) {
      const userData = data.data as IUserInfo;
      return userData.username;
    }
    return "";
  }, [data]);

  const branchName = React.useMemo(() => {
    if (data?.data) {
      const userData = data.data as IUserInfo;
      return userData.branch;
    }
    return "";
  }, [data]);

  const staffName = React.useMemo(() => {
    if (data?.data) {
      const userData = data.data as IUserInfo;
      return userData.staff;
    }
    return "";
  }, [data]);

  const allcliendList = React.useMemo(() => {
    return (allclientData?.data ?? []) as ICustomer[];
  }, [allclientData?.data]);

  const notificationList: IToDo[] = Array.isArray(noificationData?.data)
    ? (noificationData?.data as IToDo[])
    : [];

  const unreadCount = notificationList.reduce(
    (total, notif) => total + (notif.unread_count || 0),
    0
  );

  const clientMap = useMemo(() => {
    const map = new Map();
    allcliendList?.forEach((category) => {
      map.set(
        category.id,
        toProperCase(category.customerFirstName || "") +
          " " +
          toProperCase(category.customerLastName || "")
      );
    });
    return map;
  }, [allcliendList]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentMenuName]);
  // console.log(staffName);

  // React.useEffect(() => {
  //   for (const mainMenu of MainMenuList) {
  //     for (const menu of mainMenu.menuList || []) {
  //       if (menu.path === location.pathname) {
  //         setCurrentMenuName(menu.path);
  //         setSelectedSubmenu("");
  //         setExpandMenu(menu.title);
  //       }

  //       for (const submenu of menu.submenus || []) {
  //         if (submenu.path === location.pathname) {
  //           setSelectedSubmenu(submenu.path);
  //           setExpandMenu(menu.title);
  //         }
  //       }
  //     }
  //   }
  // }, [location.pathname]);

  React.useEffect(() => {
    let found = false;
    for (const mainMenu of MainMenuList) {
      for (const menu of mainMenu.menuList || []) {
        if (menu.path === location.pathname) {
          setCurrentMenuName(menu.path);
          setSelectedSubmenu("");
          setExpandMenu(menu.title);
          found = true;
        }

        for (const submenu of menu.submenus || []) {
          if (submenu.path === location.pathname) {
            setSelectedSubmenu(submenu.path);
            setExpandMenu(menu.title);
            setHoveredSubmenu(submenu.path);
            found = true;
          }
        }

        if (found) break;
      }
      if (found) break;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const findTopBarTitle = (): string => {
      // Check custom routes first

      if (location.pathname.startsWith("/product-history/")) {
        return "Product Details";
      }
      if (location.pathname.startsWith("/department/create")) {
        return "Create Department";
      }
      if (location.pathname.startsWith("/store/create")) {
        return "Create Store";
      }
      if (location.pathname.startsWith("/category/create")) {
        return "Create Category";
      }
      if (location.pathname.startsWith("/product/create")) {
        return "Create Items";
      }
      if (location.pathname.startsWith("/branch/create")) {
        return "Create Branch";
      }
      if (location.pathname.startsWith("/branch/edit/")) {
        return "Edit Branch";
      }
      if (location.pathname.startsWith("/printer/create")) {
        return "Create Printer ";
      }
      if (location.pathname.startsWith("/tax/create")) {
        return "Create Tax";
      }
      if (location.pathname.startsWith("/user/privilege/create")) {
        return "Create Privilege";
      }
      if (location.pathname.startsWith("/user/role/create")) {
        return "Create User Role";
      }
      if (location.pathname.startsWith("/user/create")) {
        return "Create User";
      }
      if (location.pathname.startsWith("/main/item/view/")) {
        return "Main Item view";
      }
      if (location.pathname.startsWith("/main/item/create")) {
        return "Create Main Item";
      }
      if (location.pathname.startsWith("/tag/create")) {
        return "Create Tag";
      }
      if (location.pathname.startsWith("/brand/create")) {
        return "Create Brand";
      }
      if (location.pathname.startsWith("/order/list/create")) {
        return "Create Order List";
      }
      if (location.pathname.startsWith("/order/list/edit")) {
        return "Edit Order List";
      }
      if (location.pathname.startsWith("/team/create")) {
        return "Create Staff";
      }
      if (location.pathname.startsWith("/team-history/")) {
        return "View Staff";
      }
      if (location.pathname.startsWith("/re-Schedule/create?date=")) {
        return "Create Reschedule ";
      }
      if (location.pathname.startsWith("/attendance/weekly/report")) {
        return "Schedule Report";
      }
      if (location.pathname.startsWith("/re-Schedule/create")) {
        return "Create Reschedule ";
      }
      if (location.pathname.startsWith("/re-Schedule/list")) {
        return "Reschedule List";
      }
      if (location.pathname.startsWith("/payroll/generate")) {
        return "Payroll Generate";
      }
      if (location.pathname.startsWith("/payroll/list/edit/")) {
        return "Payroll Edit";
      }
      if (location.pathname.startsWith("/position/create")) {
        return "Position Create";
      }

      if (location.pathname.startsWith("/customer-history/")) {
        return "Client Details";
      }
      if (location.pathname.startsWith("/customer-deals/")) {
        return "Deals";
      }
      if (location.pathname.startsWith("/policy-history/")) {
        return "policy Details";
      }
      if (
        location.pathname.startsWith(
          "/profile/customer/:customerId/policy/:policyId"
        )
      ) {
        return "Profile";
      }
      if (location.pathname.startsWith("/profile/customer/")) {
        return "Profile";
      }

      if (location.pathname.startsWith("/looping/create")) {
        return "Create Looping";
      }
      if (location.pathname.startsWith("/coursing/create")) {
        return "Create Coursing";
      }
      if (location.pathname.startsWith("/brand/create")) {
        return "Create Insurance Company";
      }
      if (location.pathname.startsWith("/tag/create")) {
        return "Create Product Tag";
      }
      if (location.pathname.startsWith("/tax/create")) {
        return "Create Tax";
      }
      if (location.pathname.startsWith("/printer/create")) {
        return "Create Printer";
      }
      if (location.pathname.startsWith("/customer/create")) {
        return "Create Client";
      }
      if (location.pathname.startsWith("/policy/create")) {
        return "Create Policy";
      }
      if (location.pathname.startsWith("/category/create")) {
        return "Create Coverage";
      }
      if (location.pathname.startsWith("/deal/")) {
        return "Deal Edit";
      }
      if (location.pathname.startsWith("/summary-report/")) {
        return "Summary Report";
      }
      if (location.pathname.startsWith("/summary/pdf/")) {
        return "Generate Pdf";
      }
      if (location.pathname.startsWith("/client/meeting/create")) {
        return "Create Meeting";
      }
      if (location.pathname.startsWith("/conversations/create")) {
        return "Create Conversations";
      }
      if (location.pathname.startsWith("/conversations/messages")) {
        return "Messages";
      }
      if (location.pathname.startsWith("/client/meeting/setting")) {
        return "Meeting Setting";
      }
      if (location.pathname.startsWith("/email/setting/create")) {
        return "Create Email Setting";
      }
      if (location.pathname.startsWith("/email/template/create")) {
        return "Manage Template";
      }
      if (location.pathname.startsWith("/newsletters/create")) {
        return "Create Newsletter";
      }
      if (location.pathname.startsWith("/client_type/create")) {
        return "Create Client Type";
      }
      if (location.pathname.startsWith("/client/newsletters/view")) {
        return "View Client Newsletter E-mail";
      }
      // if (location.pathname.startsWith("/client/newsletters/list")) {
      //   return "Newsletter List";
      // }
      if (location.pathname.startsWith("/client/newsletters/sms/view")) {
        return "View Client Newsletter Sms";
      }
      if (location.pathname.startsWith("/client/newsletters/sms/list")) {
        return "Client Newsletter Sms List";
      }
      if (location.pathname.startsWith("/client/task/create")) {
        return "Create Task";
      }
      if (location.pathname.startsWith("/client/task/setting")) {
        return "Task Setting";
      }
      if (location.pathname.startsWith("/workshop/create")) {
        return "Register Workshop";
      }
      if (location.pathname.startsWith("/workshop/history/")) {
        return "Workshop Record History";
      }
      if (location.pathname.startsWith("/workshop/newsletter/e-mail/history")) {
        return "Workshop E-mail History";
      }
      if (location.pathname.startsWith("/workshop/newsletter/e-mail/view/")) {
        return "Workshop E-mail Record View";
      }
      if (location.pathname.startsWith("/workshop/newsletter/e-mail")) {
        return "Sent Workshop E-mail";
      }
      if (location.pathname.startsWith("/client/newsletter/e-mail")) {
        return "Sent E-mail";
      }
      if (location.pathname.startsWith("/client/newsletter/sms")) {
        return "Sent Sms";
      }
      if (location.pathname.startsWith("/workshop/feedback/list")) {
        return "Workshop FeedBack History";
      }
      // Check MainMenuList for matching path
      for (const mainMenu of MainMenuList) {
        for (const menu of mainMenu.menuList || []) {
          if (menu.path === location.pathname) {
            return menu.topBarTitle;
          }

          for (const submenu of menu.submenus || []) {
            if (submenu.path === location.pathname) {
              return submenu.topBarTitle || "";
            }
          }
        }
      }

      // Default title if no matches are found
      return "";
    };

    setTopBarTitle(findTopBarTitle());
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen(!open);
    setExpandMenu("");
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleClickNotificationPopUp = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setNotificationPopUp(event.currentTarget);
  };

  const handleClickProfilePopUp = (event: React.MouseEvent<HTMLElement>) => {
    setProfilePopUp(event.currentTarget);
  };

  const handleCloseProfilePopUp = () => {
    setProfilePopUp(null);
  };

  const handleCloseNotificationPopUp = () => {
    setNotificationPopUp(null);
  };

  const handleImgClick = () => {
    navigate("/dashboard");
  };

  const handleMainMenuItemClick = (ml: menuList) => {
    if (ml.submenus && ml.submenus.length > 0) {
      setExpandMenu(ml.title === expandMenu ? "" : ml.title);
    } else if (ml.path) {
      setCurrentMenuName(ml.path);
      setSelectedSubmenu("");
      navigate(ml.path);
      setMobileDrawerOpen(false);
    }
    setTopBarTitle(ml.topBarTitle || "");
  };

  const handleSubmenuItemClicked = (submenu: SubmenuItem) => {
    setCurrentMenuName(submenu.path);
    setSelectedSubmenu(submenu.path);
    navigate(submenu.path);
    setTopBarTitle(submenu.topBarTitle || "");
    setMobileDrawerOpen(false);
  };

  const lastCustomerId = localStorage.getItem("lastCustomerId");

  return (
    <Box sx={{ display: "flex" }}>
      {!isMobile ? (
        <Box sx={{ display: "flex" }}>
          {currentMenuNameFullScreen === "/order/list/create" ||
          currentMenuNameFullScreen === "/order/list/edit/:id" ||
          currentMenuNameFullScreen ===
            "/customer/:customerId/policy/:policyNumber" ||
          currentMenuNameFullScreen === "/order/list/sentToManager/edit/:id" ? (
            <>
              <AppBar position="fixed" className={classes.mobileTopbar}>
                <Toolbar className={classes.toolbar}>
                  <IconButton
                    onClick={handleMobileDrawerToggle}
                    edge="start"
                    sx={{
                      ...(mobileDrawerOpen && { display: "none" }),
                    }}
                    className={classes.topbarButton}
                  >
                    {mobileDrawerOpen ? (
                      <CloseIcon className={classes.topbarIcon} />
                    ) : (
                      <MenuIcon className={classes.topbarIcon} />
                    )}
                  </IconButton>
                  <Typography
                    width={"100%"}
                    className={classes.h4TextTopBar}
                    pl={5}
                  >
                    {topBarTitle}
                  </Typography>
                  {/* <Grid container alignItems="center">
                    <Grid item>
                      {lastCustomerId && (
                        <Link
                          to={`/profile/customer/${lastCustomerId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonSearchRoundedIcon
                              fontSize="medium"
                              color="primary"
                            />
                            <Typography
                              width={"100%"}
                              className={classes.h4QuotoTopBarSearch}
                            >
                              {clientMap.get(Number(lastCustomerId)) || ""}
                            </Typography>
                          </div>
                        </Link>
                      )}
                    </Grid>
                  </Grid> */}
                  <Grid
                    container
                    data-testid="user-content"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ display: "flex" }}
                  >
                    <Grid item>
                      {lastCustomerId && (
                        <Link
                          to={`/profile/customer/${lastCustomerId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonSearchRoundedIcon
                              fontSize="medium"
                              color="primary"
                            />
                            <Typography
                              width={"100%"}
                              className={classes.h4QuotoTopBarSearch}
                            >
                              {clientMap.get(Number(lastCustomerId)) || ""}
                            </Typography>
                          </div>
                        </Link>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={handleClickNotificationPopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Badge
                          badgeContent={unreadCount > 0 ? unreadCount : null}
                          overlap="circular"
                          color="error"
                          max={99}
                        >
                          <MdOutlineNotificationsNone
                            className={classes.topbarIcon}
                          />
                        </Badge>
                      </IconButton>
                      <IconButton
                        onClick={toggleTheme}
                        aria-haspopup="true"
                        className={classes.topbarButton}
                      >
                        {mode == "light" ? (
                          <MdDarkMode className={classes.topbarIcon} />
                        ) : (
                          <MdDarkMode className={classes.topbarIcon} />
                        )}
                      </IconButton>
                    </Grid>

                    <Grid item>
                      {/* <Typography className={classes.h4QuotoTopBar}>
                        {branchName && userRole && staffName
                          ? `${branchName} / ${userRole} / ${staffName}`
                          : branchName || userRole || staffName || ""}{" "}
                      </Typography> */}
                      <Typography className={classes.h4QuotoTopBar}>
                        {[branchName, userRole].filter(Boolean).join(" / ")}{" "}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={handleClickProfilePopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Avatar
                          alt="Travis Howard"
                          src={UserImg}
                          className={classes.topbarIconProfile}
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Profile
                    profile={profilePopUp}
                    onClose={handleCloseProfilePopUp}
                    open={openProfilePopUp}
                    username={username}
                    staffName={staffName}
                  />
                  <Notification
                    notification={notificationPopUp}
                    onClose={handleCloseNotificationPopUp}
                    open={openNotificationPopUp}
                    notificationData={notificationList}
                  />
                </Toolbar>

                <>
                  <MobileDrawer
                    open={mobileDrawerOpen}
                    onClose={handleMobileDrawerClose}
                  >
                    <DrawerHeader>
                      {open && (
                        <img
                          src={LogoMain}
                          alt="AttoDesk Login"
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                            cursor: "pointer",
                          }}
                          onClick={handleImgClick}
                        />
                      )}
                      <IconButton
                        className={classes.topbarButton}
                        onClick={handleMobileDrawerClose}
                      >
                        {theme.direction === "rtl" ? (
                          <ChevronRightIcon className={classes.topbarIcon} />
                        ) : (
                          <ChevronLeftIcon className={classes.topbarIcon} />
                        )}
                      </IconButton>
                    </DrawerHeader>
                    <DrawerContent sx={{ padding: open ? 3 : 0 }}>
                      {MainMenuList.map((ml, index) => {
                        const hasPrivilege =
                          ml.menuList?.some((menuItem) =>
                            userHasRight(menuItem.privilege)
                          ) ||
                          ml.menuList?.some((menuItem) =>
                            menuItem.submenus?.some((submenu) =>
                              userHasRight(submenu.privilege)
                            )
                          );

                        return (
                          <React.Fragment key={index}>
                            {/* {index === 0 && open && (
                              <Grid
                                container
                                spacing={2}
                                justifyContent="center"
                                alignItems="center"
                                textAlign="center"
                              >
                                <List
                                  sx={{
                                    borderBottom: `1px solid ${theme.palette.background.default}`,
                                    paddingBottom: 2,
                                    paddingTop: 2,
                                    marginBottom: open ? 3 : 0,
                                  }}
                                >
                                  <ListItemButton>
                                    <ListItemIcon>
                                      <Avatar
                                        alt="Travis Howard"
                                        src={UserImg}
                                        className={classes.topbarIconProfileImg}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      className={classes.h4QuotoImg}
                                    >
                                      {username}
                                    </ListItemText>
                                  </ListItemButton>
                                </List>
                              </Grid>
                            )} */}
                            {index === 0 && open && (
                              <Box sx={{ height: 20 }}></Box>
                            )}
                            {open && hasPrivilege && (
                              <Box>
                                <Typography className={classes.menuTitle}>
                                  {ml.comomTitle}
                                </Typography>
                              </Box>
                            )}
                            {ml.menuList && (
                              <List
                                sx={{
                                  // borderBottom: "1px solid #878e9f",
                                  // paddingBottom: 5,
                                  // marginBottom: open ? 5 : 0,
                                  borderBottom:
                                    open && hasPrivilege
                                      ? "1px solid #878e9f"
                                      : "none",
                                  paddingBottom: open && hasPrivilege ? 5 : 0,
                                  marginBottom: open && hasPrivilege ? 5 : 0,
                                }}
                              >
                                {ml.menuList
                                  .filter((menuList) =>
                                    userHasRight(menuList.privilege)
                                  ) // Filter by privilege
                                  .map((menuList) => (
                                    <ListItem
                                      button
                                      key={menuList.title}
                                      disablePadding
                                      sx={{
                                        display: "block",
                                      }}
                                      onMouseEnter={() =>
                                        setHoveredMenuName(menuList.path)
                                      }
                                      onMouseLeave={() =>
                                        setHoveredMenuName("")
                                      }
                                    >
                                      <ListItemButton
                                        component={
                                          menuList.submenus &&
                                          menuList.submenus.length > 0
                                            ? "div"
                                            : Link
                                        }
                                        to={
                                          menuList.submenus &&
                                          menuList.submenus.length > 0
                                            ? undefined
                                            : menuList.path
                                        }
                                        target="_self"
                                        onClick={() =>
                                          handleMainMenuItemClick(menuList)
                                        }
                                        sx={{
                                          transition: "none",
                                          "&:hover": {
                                            transition: "none",
                                            "& .MuiListItemIcon-root, & .MuiTypography-root":
                                              {
                                                color:
                                                  theme.palette.mainMenuNav
                                                    .hoverColorNavBg,
                                                transition: "none",
                                              },
                                          },
                                          "& .MuiListItemIcon-root": {
                                            color:
                                              selectedSubmenu?.startsWith(
                                                menuList.path
                                              ) ||
                                              hoveredMenuName ===
                                                menuList.path ||
                                              currentMenuName === menuList.path
                                                ? theme.palette.background
                                                    .default
                                                : currentMenuName ===
                                                  menuList.path
                                                ? theme.palette.mainMenuNav
                                                    .hoverColorNavBg
                                                : theme.palette.topbarButton
                                                    .drawerMenuText,
                                            opacity:
                                              selectedSubmenu?.startsWith(
                                                menuList.path
                                              ) ||
                                              hoveredMenuName ===
                                                menuList.path ||
                                              currentMenuName === menuList.path
                                                ? 1
                                                : 0.6,
                                            transition: "none",
                                          },
                                          "& .MuiTypography-root": {
                                            color:
                                              selectedSubmenu?.startsWith(
                                                menuList.path
                                              ) ||
                                              hoveredMenuName ===
                                                menuList.path ||
                                              currentMenuName === menuList.path
                                                ? theme.palette.mainMenuNav
                                                    .hoverColorNavBg
                                                : currentMenuName ===
                                                  menuList.path
                                                ? theme.palette.mainMenuNav
                                                    .hoverColorNavBg
                                                : theme.palette.topbarButton
                                                    .drawerMenuText,
                                            opacity:
                                              selectedSubmenu?.startsWith(
                                                menuList.path
                                              ) ||
                                              hoveredMenuName ===
                                                menuList.path ||
                                              currentMenuName === menuList.path
                                                ? 1
                                                : 0.6,
                                            transition: "none",
                                          },
                                          borderLeft:
                                            selectedSubmenu?.startsWith(
                                              menuList.path
                                            ) ||
                                            currentMenuName === menuList.path
                                              ? `3px solid ${theme.palette.background.default}`
                                              : currentMenuName ===
                                                menuList.path
                                              ? "none"
                                              : "none",
                                        }}
                                      >
                                        <ListItemIcon
                                          className={
                                            classes.listItemIcomMainMenu
                                          }
                                        >
                                          {menuList.icon as React.ReactNode}
                                        </ListItemIcon>
                                        <ListItemText>
                                          {menuList.title}
                                        </ListItemText>
                                        <ListItemIcon
                                          className={
                                            classes.listItemIcomMainMenu
                                          }
                                          sx={{
                                            display: "flex",
                                            textAlign: "end",
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          {menuList.submenus &&
                                            (expandMenu === menuList.title ? (
                                              <ExpandLessIcon
                                                sx={{
                                                  fontSize: "18px",
                                                  display: "flex",
                                                  textAlign: "end",
                                                  justifyContent: "flex-end",
                                                  "&:hover": {
                                                    color:
                                                      theme.palette.mainMenuNav
                                                        .hoverColorNavBg,
                                                  },
                                                  color:
                                                    selectedSubmenu?.startsWith(
                                                      menuList.path
                                                    ) ||
                                                    hoveredMenuName ===
                                                      menuList.path ||
                                                    currentMenuName ===
                                                      menuList.path
                                                      ? theme.palette
                                                          .mainMenuNav
                                                          .hoverColorNavBg
                                                      : currentMenuName ===
                                                        menuList.path
                                                      ? theme.palette
                                                          .mainMenuNav
                                                          .hoverColorNavBg
                                                      : theme.palette
                                                          .topbarButton
                                                          .drawerMenuText,
                                                  opacity:
                                                    selectedSubmenu?.startsWith(
                                                      menuList.path
                                                    ) ||
                                                    hoveredMenuName ===
                                                      menuList.path ||
                                                    currentMenuName ===
                                                      menuList.path
                                                      ? 1
                                                      : 0.6,
                                                }}
                                              />
                                            ) : (
                                              <ExpandMoreIcon
                                                sx={{
                                                  fontSize: "18px",
                                                  display: "flex",
                                                  textAlign: "end",
                                                  justifyContent: "flex-end",
                                                  "&:hover": {
                                                    color:
                                                      theme.palette.mainMenuNav
                                                        .hoverColorNavBg,
                                                  },
                                                  color:
                                                    selectedSubmenu?.startsWith(
                                                      menuList.path
                                                    ) ||
                                                    hoveredMenuName ===
                                                      menuList.path ||
                                                    currentMenuName ===
                                                      menuList.path
                                                      ? theme.palette
                                                          .mainMenuNav
                                                          .hoverColorNavBg
                                                      : currentMenuName ===
                                                        menuList.path
                                                      ? theme.palette
                                                          .mainMenuNav
                                                          .hoverColorNavBg
                                                      : theme.palette
                                                          .topbarButton
                                                          .drawerMenuText,
                                                  opacity:
                                                    selectedSubmenu?.startsWith(
                                                      menuList.path
                                                    ) ||
                                                    hoveredMenuName ===
                                                      menuList.path ||
                                                    currentMenuName ===
                                                      menuList.path
                                                      ? 1
                                                      : 0.6,
                                                }}
                                              />
                                            ))}
                                        </ListItemIcon>
                                      </ListItemButton>
                                      {menuList.submenus && (
                                        <Collapse
                                          in={expandMenu === menuList.title}
                                          timeout="auto"
                                          unmountOnExit
                                        >
                                          <List>
                                            {menuList.submenus
                                              .filter((submenu) =>
                                                userHasRight(submenu.privilege)
                                              )
                                              .map((submenu) => (
                                                <ListItem
                                                  disablePadding
                                                  key={submenu.title}
                                                  onMouseEnter={() =>
                                                    setHoveredSubmenu(
                                                      submenu.path
                                                    )
                                                  }
                                                  onMouseLeave={() =>
                                                    setHoveredSubmenu("")
                                                  }
                                                >
                                                  <ListItemButton
                                                    component={Link}
                                                    to={submenu.path}
                                                    target="_self"
                                                    onClick={() =>
                                                      handleSubmenuItemClicked(
                                                        submenu
                                                      )
                                                    }
                                                    sx={{
                                                      pl: "72px",
                                                      "&:hover": {
                                                        "& .MuiListItemIcon-root, & .MuiTypography-root":
                                                          {
                                                            color:
                                                              theme.palette
                                                                .mainMenuNav
                                                                .hoverColorNavBg,
                                                          },
                                                      },
                                                      "& .MuiTypography-root": {
                                                        color:
                                                          hoveredSubmenu ===
                                                            submenu.path ||
                                                          currentMenuName ===
                                                            submenu.path
                                                            ? theme.palette
                                                                .mainMenuNav
                                                                .hoverColorNavBg
                                                            : theme.palette
                                                                .topbarButton
                                                                .drawerMenuText,
                                                        opacity:
                                                          hoveredSubmenu ===
                                                            submenu.path ||
                                                          currentMenuName ===
                                                            submenu.path
                                                            ? 1
                                                            : 0.6,
                                                      },
                                                    }}
                                                  >
                                                    <ListItemText>
                                                      <Typography>
                                                        {submenu.title}
                                                      </Typography>
                                                    </ListItemText>
                                                  </ListItemButton>
                                                </ListItem>
                                              ))}
                                          </List>
                                        </Collapse>
                                      )}
                                    </ListItem>
                                  ))}
                              </List>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </DrawerContent>
                  </MobileDrawer>
                </>
              </AppBar>
            </>
          ) : (
            <>
              <CssBaseline />
              <AppBar position="fixed" open={open} className={classes.topbar}>
                <Toolbar className={classes.toolbar}>
                  <Typography width={"100%"} className={classes.h4TextTopBar}>
                    {topBarTitle}
                  </Typography>
                  {/* <Grid container alignItems="center">
                    <Grid item>
                      {lastCustomerId && (
                        <Link
                          to={`/profile/customer/${lastCustomerId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonSearchRoundedIcon
                              fontSize="medium"
                              color="primary"
                            />

                            <Typography
                              width={"100%"}
                              className={classes.h4QuotoTopBarSearch}
                            >
                              {clientMap.get(Number(lastCustomerId)) || ""}
                            </Typography>
                          </div>
                        </Link>
                      )}
                    </Grid>
                  </Grid> */}
                  <Grid
                    container
                    data-testid="user-content"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ display: "flex" }}
                  >
                    <Grid item>
                      {lastCustomerId && (
                        <Link
                          to={`/profile/customer/${lastCustomerId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonSearchRoundedIcon
                              fontSize="medium"
                              color="primary"
                            />

                            <Typography
                              width={"100%"}
                              className={classes.h4QuotoTopBarSearch}
                            >
                              {clientMap.get(Number(lastCustomerId)) || ""}
                            </Typography>
                          </div>
                        </Link>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={handleClickNotificationPopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Badge
                          badgeContent={unreadCount > 0 ? unreadCount : null}
                          overlap="circular"
                          color="error"
                          max={99}
                        >
                          <MdOutlineNotificationsNone
                            className={classes.topbarIcon}
                          />
                        </Badge>
                      </IconButton>
                      <IconButton
                        onClick={toggleTheme}
                        aria-haspopup="true"
                        className={classes.topbarButton}
                      >
                        {mode == "light" ? (
                          <MdDarkMode className={classes.topbarIcon} />
                        ) : (
                          <MdDarkMode className={classes.topbarIcon} />
                        )}
                      </IconButton>
                    </Grid>

                    <Grid item>
                      <Typography className={classes.h4QuotoTopBar}>
                        {[branchName, userRole].filter(Boolean).join(" / ")}{" "}
                      </Typography>
                    </Grid>

                    <Grid item>
                      <IconButton
                        onClick={handleClickProfilePopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Avatar
                          alt="Travis Howard"
                          src={UserImg}
                          className={classes.topbarIconProfile}
                        />
                      </IconButton>
                    </Grid>

                    {/* <Grid item>
                  <div className={classes.appSearch}>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CiSearch className={classes.searchWidgetIcon} />
                          </InputAdornment>
                        ),
                        classes: {
                          root: classes.formControl,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                      fullWidth
                      placeholder="Search .."
                      variant="outlined"
                      size="small"
                    />
                  </div>
                </Grid> */}
                  </Grid>
                  <Profile
                    profile={profilePopUp}
                    onClose={handleCloseProfilePopUp}
                    open={openProfilePopUp}
                    username={username}
                    staffName={staffName}
                  />
                  <Notification
                    notification={notificationPopUp}
                    onClose={handleCloseNotificationPopUp}
                    open={openNotificationPopUp}
                    notificationData={notificationList}
                  />
                </Toolbar>
              </AppBar>
              <IsNotMobileDrawer variant="permanent" open={open}>
                <DrawerHeader>
                  {open ? (
                    <img
                      src={LogoMain}
                      alt="AttoDesk Login"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        cursor: "pointer",
                      }}
                      onClick={handleImgClick}
                    />
                  ) : (
                    ""
                  )}

                  <IconButton
                    className={classes.topbarButton}
                    onClick={handleDrawerToggle}
                  >
                    {open ? (
                      <ChevronRightIcon className={classes.topbarIcon} />
                    ) : (
                      <ChevronLeftIcon className={classes.topbarIcon} />
                    )}
                  </IconButton>
                </DrawerHeader>

                <DrawerContent sx={{ padding: open ? 1 : 0 }}>
                  {MainMenuList.map((ml, index) => {
                    const hasPrivilege =
                      ml.menuList?.some((menuItem) =>
                        userHasRight(menuItem.privilege)
                      ) ||
                      ml.menuList?.some((menuItem) =>
                        menuItem.submenus?.some((submenu) =>
                          userHasRight(submenu.privilege)
                        )
                      );

                    return (
                      <Grid>
                        <React.Fragment key={index}>
                          {/* {index === 0 && open && (
                            <Grid
                              container
                              spacing={2}
                              justifyContent="center"
                              alignItems="center"
                              textAlign="center"
                            >
                              <List
                                sx={{
                                  borderBottom: `1px solid ${theme.palette.background.default}`,
                                  paddingBottom: 2,
                                  paddingTop: 4,
                                  marginBottom: open ? 3 : 0,
                                }}
                              >
                                <ListItemButton>
                                  <ListItemIcon>
                                    <Avatar
                                      alt="Travis Howard"
                                      src={UserImg}
                                      className={classes.topbarIconProfileImg}
                                    />
                                  </ListItemIcon>
                                  <ListItemText className={classes.h4QuotoImg}>
                                    {username}
                                  </ListItemText>
                                </ListItemButton>
                              </List>
                            </Grid>
                          )} */}
                          {index === 0 && open && (
                            <Box sx={{ height: 20 }}></Box>
                          )}
                          {open && hasPrivilege && (
                            <Box>
                              <Typography className={classes.menuTitle}>
                                {ml.comomTitle}
                              </Typography>
                            </Box>
                          )}
                          {ml.menuList && (
                            <List
                              sx={{
                                // borderBottom: "1px solid #878e9f",
                                // paddingBottom: 5,
                                // marginBottom: open ? 5 : 0,
                                borderBottom:
                                  open && hasPrivilege
                                    ? `1px solid ${theme.palette.topbarButton.drawerMenuText}`
                                    : "none",
                                paddingBottom: open && hasPrivilege ? 5 : 0,
                                marginBottom: open && hasPrivilege ? 5 : 0,
                              }}
                            >
                              {ml.menuList
                                .filter((menuItem) =>
                                  userHasRight(menuItem.privilege)
                                ) // Filter by privilege
                                .map((menuItem) => (
                                  <ListItem
                                    button
                                    key={menuItem.title}
                                    disablePadding
                                    sx={{
                                      display: "block",
                                    }}
                                    onMouseEnter={() =>
                                      setHoveredMenuName(menuItem.path)
                                    }
                                    onMouseLeave={() => setHoveredMenuName("")}
                                  >
                                    <ListItemButton
                                      // component={Link}
                                      // to={menuItem.path}
                                      component={
                                        menuItem.submenus &&
                                        menuItem.submenus.length > 0
                                          ? "div"
                                          : Link
                                      }
                                      to={
                                        menuItem.submenus &&
                                        menuItem.submenus.length > 0
                                          ? undefined
                                          : menuItem.path
                                      }
                                      target="_self"
                                      onClick={() =>
                                        handleMainMenuItemClick(menuItem)
                                      }
                                      sx={{
                                        transition: "none",
                                        "&:hover": {
                                          transition: "none",
                                          "& .MuiListItemIcon-root, & .MuiTypography-root":
                                            {
                                              color:
                                                theme.palette.mainMenuNav
                                                  .hoverColorNavBg,
                                              transition: "none",
                                            },
                                        },
                                        "& .MuiListItemIcon-root": {
                                          color:
                                            selectedSubmenu?.startsWith(
                                              menuItem.path
                                            ) ||
                                            hoveredMenuName === menuItem.path ||
                                            currentMenuName === menuItem.path
                                              ? theme.palette.background.default
                                              : currentMenuName ===
                                                menuItem.path
                                              ? theme.palette.mainMenuNav
                                                  .hoverColorNavBg
                                              : theme.palette.topbarButton
                                                  .drawerMenuText,
                                          opacity:
                                            selectedSubmenu?.startsWith(
                                              menuItem.path
                                            ) ||
                                            hoveredMenuName === menuItem.path ||
                                            currentMenuName === menuItem.path
                                              ? 1
                                              : 0.6,
                                          transition: "none",
                                        },
                                        "& .MuiTypography-root": {
                                          color:
                                            selectedSubmenu?.startsWith(
                                              menuItem.path
                                            ) ||
                                            hoveredMenuName === menuItem.path ||
                                            currentMenuName === menuItem.path
                                              ? theme.palette.mainMenuNav
                                                  .hoverColorNavBg
                                              : currentMenuName ===
                                                menuItem.path
                                              ? theme.palette.mainMenuNav
                                                  .hoverColorNavBg
                                              : theme.palette.topbarButton
                                                  .drawerMenuText,
                                          opacity:
                                            selectedSubmenu?.startsWith(
                                              menuItem.path
                                            ) ||
                                            hoveredMenuName === menuItem.path ||
                                            currentMenuName === menuItem.path
                                              ? 1
                                              : 0.6,
                                          transition: "none",
                                        },
                                        borderLeft:
                                          selectedSubmenu?.startsWith(
                                            menuItem.path
                                          ) || currentMenuName === menuItem.path
                                            ? `3px solid ${theme.palette.background.default}`
                                            : currentMenuName === menuItem.path
                                            ? "none"
                                            : "none",
                                      }}
                                    >
                                      <ListItemIcon
                                        className={classes.listItemIcomMainMenu}
                                      >
                                        {menuItem.icon as React.ReactNode}
                                      </ListItemIcon>
                                      <ListItemText>
                                        {menuItem.title}
                                      </ListItemText>
                                      <ListItemIcon
                                        className={classes.listItemIcomMainMenu}
                                        sx={{
                                          display: "flex",
                                          textAlign: "end",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        {menuItem.submenus &&
                                          (expandMenu === menuItem.title ? (
                                            <ExpandLessIcon
                                              sx={{
                                                fontSize: "18px",
                                                display: "flex",
                                                textAlign: "end",
                                                justifyContent: "flex-end",
                                                "&:hover": {
                                                  color:
                                                    theme.palette.mainMenuNav
                                                      .hoverColorNavBg,
                                                },
                                                color:
                                                  selectedSubmenu?.startsWith(
                                                    menuItem.path
                                                  ) ||
                                                  hoveredMenuName ===
                                                    menuItem.path ||
                                                  currentMenuName ===
                                                    menuItem.path
                                                    ? theme.palette.mainMenuNav
                                                        .hoverColorNavBg
                                                    : currentMenuName ===
                                                      menuItem.path
                                                    ? theme.palette.mainMenuNav
                                                        .hoverColorNavBg
                                                    : theme.palette.topbarButton
                                                        .drawerMenuText,
                                                opacity:
                                                  selectedSubmenu?.startsWith(
                                                    menuItem.path
                                                  ) ||
                                                  hoveredMenuName ===
                                                    menuItem.path ||
                                                  currentMenuName ===
                                                    menuItem.path
                                                    ? 1
                                                    : 0.6,
                                              }}
                                            />
                                          ) : (
                                            <ExpandMoreIcon
                                              sx={{
                                                fontSize: "18px",
                                                display: "flex",
                                                textAlign: "end",
                                                justifyContent: "flex-end",
                                                "&:hover": {
                                                  color:
                                                    theme.palette.mainMenuNav
                                                      .hoverColorNavBg,
                                                },
                                                color:
                                                  selectedSubmenu?.startsWith(
                                                    menuItem.path
                                                  ) ||
                                                  hoveredMenuName ===
                                                    menuItem.path ||
                                                  currentMenuName ===
                                                    menuItem.path
                                                    ? theme.palette.mainMenuNav
                                                        .hoverColorNavBg
                                                    : currentMenuName ===
                                                      menuItem.path
                                                    ? theme.palette.mainMenuNav
                                                        .hoverColorNavBg
                                                    : theme.palette.topbarButton
                                                        .drawerMenuText,
                                                opacity:
                                                  selectedSubmenu?.startsWith(
                                                    menuItem.path
                                                  ) ||
                                                  hoveredMenuName ===
                                                    menuItem.path ||
                                                  currentMenuName ===
                                                    menuItem.path
                                                    ? 1
                                                    : 0.6,
                                              }}
                                            />
                                          ))}
                                      </ListItemIcon>
                                    </ListItemButton>
                                    {menuItem.submenus && (
                                      <Collapse
                                        in={expandMenu === menuItem.title}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <List>
                                          {menuItem.submenus
                                            .filter((submenu) =>
                                              userHasRight(submenu.privilege)
                                            )
                                            .map((submenu) => (
                                              <ListItem
                                                disablePadding
                                                key={submenu.title}
                                                onMouseEnter={() =>
                                                  setHoveredSubmenu(
                                                    submenu.path
                                                  )
                                                }
                                                onMouseLeave={() =>
                                                  setHoveredSubmenu("")
                                                }
                                              >
                                                <ListItemButton
                                                  component={Link}
                                                  to={submenu.path}
                                                  target="_self"
                                                  onClick={() =>
                                                    handleSubmenuItemClicked(
                                                      submenu
                                                    )
                                                  }
                                                  sx={{
                                                    pl: "72px",
                                                    "&:hover": {
                                                      "& .MuiListItemIcon-root, & .MuiTypography-root":
                                                        {
                                                          color:
                                                            theme.palette
                                                              .mainMenuNav
                                                              .hoverColorNavBg,
                                                        },
                                                    },
                                                    "& .MuiTypography-root": {
                                                      color:
                                                        hoveredSubmenu ===
                                                          submenu.path ||
                                                        currentMenuName ===
                                                          submenu.path
                                                          ? theme.palette
                                                              .mainMenuNav
                                                              .hoverColorNavBg
                                                          : theme.palette
                                                              .topbarButton
                                                              .drawerMenuText,
                                                      opacity:
                                                        hoveredSubmenu ===
                                                          submenu.path ||
                                                        currentMenuName ===
                                                          submenu.path
                                                          ? 1
                                                          : 0.6,
                                                    },
                                                  }}
                                                >
                                                  <ListItemText>
                                                    <Typography>
                                                      {submenu.title}
                                                    </Typography>
                                                  </ListItemText>
                                                </ListItemButton>
                                              </ListItem>
                                            ))}
                                        </List>
                                      </Collapse>
                                    )}
                                  </ListItem>
                                ))}
                            </List>
                          )}
                        </React.Fragment>
                      </Grid>
                    );
                  })}
                </DrawerContent>
              </IsNotMobileDrawer>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex" }}>
          <AppBar position="fixed" className={classes.mobileTopbar}>
            <Toolbar className={classes.toolbar}>
              <Grid
                container
                justifyContent="space-between"
                sx={{ width: "100%" }}
                alignItems="center"
              >
                <Grid item md={1} sm={1}>
                  <IconButton
                    onClick={handleMobileDrawerToggle}
                    edge="start"
                    sx={{
                      ...(mobileDrawerOpen && { display: "none" }),
                    }}
                    className={classes.topbarButton}
                  >
                    {mobileDrawerOpen ? (
                      <CloseIcon className={classes.topbarIcon} />
                    ) : (
                      <MenuIcon className={classes.topbarIcon} />
                    )}
                  </IconButton>
                </Grid>
                <Grid item md={2} sm={2}>
                  <Typography width={"100%"} className={classes.h4TextTopBar}>
                    {topBarTitle}
                  </Typography>
                </Grid>
                {/* <Grid item md={2} sm={2}>
                  {lastCustomerId && (
                    <Link
                      to={`/profile/customer/${lastCustomerId}`}
                      className={classes.iconLinkPropNormal}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <PersonSearchRoundedIcon
                          fontSize="medium"
                          color="primary"
                        />
                        <Typography
                          width={"100%"}
                          className={classes.h4QuotoTopBarSearch}
                        >
                          {clientMap.get(Number(lastCustomerId)) || ""}
                        </Typography>
                      </div>
                    </Link>
                  )}
                </Grid> */}
                <Grid item md={7} sm={7} justifyContent="end" alignItems="end">
                  <Grid
                    container
                    data-testid="user-content"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ display: "flex" }}
                  >
                    <Grid item>
                      {lastCustomerId && (
                        <Link
                          to={`/profile/customer/${lastCustomerId}`}
                          className={classes.iconLinkPropNormal}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonSearchRoundedIcon
                              fontSize="medium"
                              color="primary"
                            />
                            <Typography
                              width={"100%"}
                              className={classes.h4QuotoTopBarSearch}
                            >
                              {clientMap.get(Number(lastCustomerId)) || ""}
                            </Typography>
                          </div>
                        </Link>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={handleClickNotificationPopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Badge
                          badgeContent={unreadCount > 0 ? unreadCount : null}
                          overlap="circular"
                          color="error"
                          max={99}
                        >
                          <MdOutlineNotificationsNone
                            className={classes.topbarIcon}
                          />
                        </Badge>
                      </IconButton>
                      <IconButton
                        onClick={toggleTheme}
                        aria-haspopup="true"
                        className={classes.topbarButton}
                      >
                        {mode == "light" ? (
                          <MdDarkMode className={classes.topbarIcon} />
                        ) : (
                          <MdDarkMode className={classes.topbarIcon} />
                        )}
                      </IconButton>
                    </Grid>
                    {/* <Grid item>
                      <Typography className={classes.h4Quoto}>
                        {username}
                      </Typography>
                    </Grid> */}
                    <Grid item>
                      <IconButton
                        onClick={handleClickProfilePopUp}
                        className={classes.topbarButton}
                        aria-haspopup="true"
                      >
                        <Avatar
                          alt="Travis Howard"
                          src={UserImg}
                          className={classes.topbarIconProfile}
                        />
                      </IconButton>
                    </Grid>

                    {/* <Grid
                      item
                      sx={{
                        [theme.breakpoints.down("sm")]: {
                          display: "none",
                        },
                      }}
                    >
                      <div className={classes.appSearch}>
                        <TextField
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CiSearch
                                  className={classes.searchWidgetIcon}
                                />
                              </InputAdornment>
                            ),
                            classes: {
                              root: classes.formControl,
                              notchedOutline: classes.notchedOutline,
                            },
                          }}
                          fullWidth
                          placeholder="Search .."
                          variant="outlined"
                          size="small"
                        />
                      </div>
                    </Grid> */}
                  </Grid>
                </Grid>
              </Grid>
              <Profile
                profile={profilePopUp}
                onClose={handleCloseProfilePopUp}
                open={openProfilePopUp}
                username={username}
                staffName={staffName}
              />
              <Notification
                notification={notificationPopUp}
                onClose={handleCloseNotificationPopUp}
                open={openNotificationPopUp}
                notificationData={notificationList}
              />
            </Toolbar>

            <>
              <MobileDrawer
                open={mobileDrawerOpen}
                onClose={handleMobileDrawerClose}
              >
                <DrawerHeader>
                  {open && (
                    <img
                      src={LogoMain}
                      alt="AttoDesk Login"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        cursor: "pointer",
                      }}
                      onClick={handleImgClick}
                    />
                  )}
                  <IconButton
                    className={classes.topbarButton}
                    onClick={handleMobileDrawerClose}
                  >
                    {theme.direction === "rtl" ? (
                      <ChevronRightIcon className={classes.topbarIcon} />
                    ) : (
                      <ChevronLeftIcon className={classes.topbarIcon} />
                    )}
                  </IconButton>
                </DrawerHeader>
                <DrawerContent sx={{ padding: open ? 1 : 0 }}>
                  {MainMenuList.map((ml, index) => {
                    const hasPrivilege =
                      ml.menuList?.some((menuItem) =>
                        userHasRight(menuItem.privilege)
                      ) ||
                      ml.menuList?.some((menuItem) =>
                        menuItem.submenus?.some((submenu) =>
                          userHasRight(submenu.privilege)
                        )
                      );

                    return (
                      <React.Fragment key={index}>
                        {/* {index === 0 && open && (
                          <Grid
                            container
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                          >
                            <List
                              sx={{
                                borderBottom: `1px solid ${theme.palette.background.default}`,
                                paddingBottom: 2,
                                paddingTop: 2,
                                marginBottom: open ? 3 : 0,
                              }}
                            >
                              <ListItemButton>
                                <ListItemIcon>
                                  <Avatar
                                    alt="Travis Howard"
                                    src={UserImg}
                                    className={classes.topbarIconProfileImg}
                                  />
                                </ListItemIcon>
                                <ListItemText className={classes.h4QuotoImg}>
                                  {username}
                                </ListItemText>
                              </ListItemButton>
                            </List>
                          </Grid>
                        )} */}
                        {index === 0 && open && <Box sx={{ height: 20 }}></Box>}
                        {open && hasPrivilege && (
                          <Box>
                            <Typography className={classes.menuTitle}>
                              {ml.comomTitle}
                            </Typography>
                          </Box>
                        )}
                        {ml.menuList && (
                          <List
                            sx={{
                              // borderBottom: "1px solid #878e9f",
                              // paddingBottom: 5,
                              // marginBottom: open ? 5 : 0,
                              borderBottom:
                                open && hasPrivilege
                                  ? "1px solid #878e9f"
                                  : "none",
                              paddingBottom: open && hasPrivilege ? 5 : 0,
                              marginBottom: open && hasPrivilege ? 5 : 0,
                            }}
                          >
                            {ml.menuList
                              .filter((menuList) =>
                                userHasRight(menuList.privilege)
                              ) // Filter by privilege
                              .map((menuList) => (
                                <ListItem
                                  button
                                  key={menuList.title}
                                  disablePadding
                                  sx={{
                                    display: "block",
                                  }}
                                  onMouseEnter={() =>
                                    setHoveredMenuName(menuList.path)
                                  }
                                  onMouseLeave={() => setHoveredMenuName("")}
                                >
                                  <ListItemButton
                                    component={
                                      menuList.submenus &&
                                      menuList.submenus.length > 0
                                        ? "div"
                                        : Link
                                    }
                                    to={
                                      menuList.submenus &&
                                      menuList.submenus.length > 0
                                        ? undefined
                                        : menuList.path
                                    }
                                    target="_self"
                                    onClick={() =>
                                      handleMainMenuItemClick(menuList)
                                    }
                                    sx={{
                                      transition: "none",
                                      "&:hover": {
                                        transition: "none",
                                        "& .MuiListItemIcon-root, & .MuiTypography-root":
                                          {
                                            color:
                                              theme.palette.mainMenuNav
                                                .hoverColorNavBg,
                                            transition: "none",
                                          },
                                      },
                                      "& .MuiListItemIcon-root": {
                                        color:
                                          selectedSubmenu?.startsWith(
                                            menuList.path
                                          ) ||
                                          hoveredMenuName === menuList.path ||
                                          currentMenuName === menuList.path
                                            ? theme.palette.background.default
                                            : currentMenuName === menuList.path
                                            ? theme.palette.mainMenuNav
                                                .hoverColorNavBg
                                            : theme.palette.topbarButton
                                                .drawerMenuText,
                                        opacity:
                                          selectedSubmenu?.startsWith(
                                            menuList.path
                                          ) ||
                                          hoveredMenuName === menuList.path ||
                                          currentMenuName === menuList.path
                                            ? 1
                                            : 0.6,
                                        transition: "none",
                                      },
                                      "& .MuiTypography-root": {
                                        color:
                                          selectedSubmenu?.startsWith(
                                            menuList.path
                                          ) ||
                                          hoveredMenuName === menuList.path ||
                                          currentMenuName === menuList.path
                                            ? theme.palette.mainMenuNav
                                                .hoverColorNavBg
                                            : currentMenuName === menuList.path
                                            ? theme.palette.mainMenuNav
                                                .hoverColorNavBg
                                            : theme.palette.topbarButton
                                                .drawerMenuText,
                                        opacity:
                                          selectedSubmenu?.startsWith(
                                            menuList.path
                                          ) ||
                                          hoveredMenuName === menuList.path ||
                                          currentMenuName === menuList.path
                                            ? 1
                                            : 0.6,
                                        transition: "none",
                                      },
                                      borderLeft:
                                        selectedSubmenu?.startsWith(
                                          menuList.path
                                        ) || currentMenuName === menuList.path
                                          ? `3px solid ${theme.palette.background.default}`
                                          : currentMenuName === menuList.path
                                          ? "none"
                                          : "none",
                                    }}
                                  >
                                    <ListItemIcon
                                      className={classes.listItemIcomMainMenu}
                                    >
                                      {menuList.icon as React.ReactNode}
                                    </ListItemIcon>
                                    <ListItemText>
                                      {menuList.title}
                                    </ListItemText>
                                    <ListItemIcon
                                      className={classes.listItemIcomMainMenu}
                                      sx={{
                                        display: "flex",
                                        textAlign: "end",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {menuList.submenus &&
                                        (expandMenu === menuList.title ? (
                                          <ExpandLessIcon
                                            sx={{
                                              fontSize: "18px",
                                              display: "flex",
                                              textAlign: "end",
                                              justifyContent: "flex-end",
                                              "&:hover": {
                                                color:
                                                  theme.palette.mainMenuNav
                                                    .hoverColorNavBg,
                                              },
                                              color:
                                                selectedSubmenu?.startsWith(
                                                  menuList.path
                                                ) ||
                                                hoveredMenuName ===
                                                  menuList.path ||
                                                currentMenuName ===
                                                  menuList.path
                                                  ? theme.palette.mainMenuNav
                                                      .hoverColorNavBg
                                                  : currentMenuName ===
                                                    menuList.path
                                                  ? theme.palette.mainMenuNav
                                                      .hoverColorNavBg
                                                  : theme.palette.topbarButton
                                                      .drawerMenuText,
                                              opacity:
                                                selectedSubmenu?.startsWith(
                                                  menuList.path
                                                ) ||
                                                hoveredMenuName ===
                                                  menuList.path ||
                                                currentMenuName ===
                                                  menuList.path
                                                  ? 1
                                                  : 0.6,
                                            }}
                                          />
                                        ) : (
                                          <ExpandMoreIcon
                                            sx={{
                                              fontSize: "18px",
                                              display: "flex",
                                              textAlign: "end",
                                              justifyContent: "flex-end",
                                              "&:hover": {
                                                color:
                                                  theme.palette.mainMenuNav
                                                    .hoverColorNavBg,
                                              },
                                              color:
                                                selectedSubmenu?.startsWith(
                                                  menuList.path
                                                ) ||
                                                hoveredMenuName ===
                                                  menuList.path ||
                                                currentMenuName ===
                                                  menuList.path
                                                  ? theme.palette.mainMenuNav
                                                      .hoverColorNavBg
                                                  : currentMenuName ===
                                                    menuList.path
                                                  ? theme.palette.mainMenuNav
                                                      .hoverColorNavBg
                                                  : theme.palette.topbarButton
                                                      .drawerMenuText,
                                              opacity:
                                                selectedSubmenu?.startsWith(
                                                  menuList.path
                                                ) ||
                                                hoveredMenuName ===
                                                  menuList.path ||
                                                currentMenuName ===
                                                  menuList.path
                                                  ? 1
                                                  : 0.6,
                                            }}
                                          />
                                        ))}
                                    </ListItemIcon>
                                  </ListItemButton>
                                  {menuList.submenus && (
                                    <Collapse
                                      in={expandMenu === menuList.title}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <List>
                                        {menuList.submenus
                                          .filter((submenu) =>
                                            userHasRight(submenu.privilege)
                                          )
                                          .map((submenu) => (
                                            <ListItem
                                              disablePadding
                                              key={submenu.title}
                                              onMouseEnter={() =>
                                                setHoveredSubmenu(submenu.path)
                                              }
                                              onMouseLeave={() =>
                                                setHoveredSubmenu("")
                                              }
                                            >
                                              <ListItemButton
                                                component={Link}
                                                to={submenu.path}
                                                target="_self"
                                                onClick={() =>
                                                  handleSubmenuItemClicked(
                                                    submenu
                                                  )
                                                }
                                                sx={{
                                                  pl: "72px",
                                                  "&:hover": {
                                                    "& .MuiListItemIcon-root, & .MuiTypography-root":
                                                      {
                                                        color:
                                                          theme.palette
                                                            .mainMenuNav
                                                            .hoverColorNavBg,
                                                      },
                                                  },
                                                  "& .MuiTypography-root": {
                                                    color:
                                                      hoveredSubmenu ===
                                                        submenu.path ||
                                                      currentMenuName ===
                                                        submenu.path
                                                        ? theme.palette
                                                            .mainMenuNav
                                                            .hoverColorNavBg
                                                        : theme.palette
                                                            .topbarButton
                                                            .drawerMenuText,
                                                    opacity:
                                                      hoveredSubmenu ===
                                                        submenu.path ||
                                                      currentMenuName ===
                                                        submenu.path
                                                        ? 1
                                                        : 0.6,
                                                  },
                                                }}
                                              >
                                                <ListItemText>
                                                  <Typography>
                                                    {submenu.title}
                                                  </Typography>
                                                </ListItemText>
                                              </ListItemButton>
                                            </ListItem>
                                          ))}
                                      </List>
                                    </Collapse>
                                  )}
                                </ListItem>
                              ))}
                          </List>
                        )}
                      </React.Fragment>
                    );
                  })}
                </DrawerContent>
              </MobileDrawer>
            </>
          </AppBar>
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // p: 3,
          // padding: currentMenuNameFullScreen === "/dashboard" ? 0 : 2,
          padding: isMobile ? 1 : 3,
          width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
        }}
      >
        <PaddingBody />
        {children}
      </Box>
    </Box>
  );
}
